import type { ComponentProps, Ref } from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

import { cn } from "../../lib/utils";
import { switchThumbVariants, switchVariants, type SwitchColor } from "./styles";

export interface SwitchProps extends ComponentProps<typeof SwitchPrimitive.Root> {
  ref?: Ref<HTMLButtonElement>;
  /**
   * Intent color of the track when the switch is on, from the same role vocabulary as `Button`'s
   * solid fill. The off state is unaffected.
   * @default "primary"
   */
  color?: SwitchColor;
  /**
   * Treatment of the off state only. `inverted` swaps the track and knob colors (a background-colored
   * track with an input-colored knob) for a switch sitting on a surface where the default gray track
   * would read as too heavy. The on state is identical in both.
   * @default "default"
   */
  offVariant?: "default" | "inverted";
}

/**
 * An on/off toggle built on Radix. Use it for a setting that takes effect immediately; use
 * `Checkbox` for a selection that is submitted with a form. Controlled via `checked` +
 * `onCheckedChange`, or uncontrolled via `defaultChecked`. Pair it with a `<Label htmlFor>` or
 * give it an `aria-label` so the switch has an accessible name.
 *
 * @example
 * ```tsx
 * <Switch id="notifications" checked={enabled} onCheckedChange={setEnabled} />
 * <Label htmlFor="notifications">Email notifications</Label>
 * <Switch color="success" aria-label="Show availability" />
 * ```
 */
export const Switch = ({ className, color, offVariant, ref, ...props }: SwitchProps) => (
  <SwitchPrimitive.Root
    ref={ref}
    data-slot="switch"
    className={cn(switchVariants({ color, offVariant }), className)}
    {...props}
  >
    <SwitchPrimitive.Thumb data-slot="switch-thumb" className={switchThumbVariants({ color, offVariant })} />
  </SwitchPrimitive.Root>
);
